import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { twMerge } from "tailwind-merge";

import { cn } from "~/lib/utils";

function Card({ ref: reference, asChild = false, className, ...props }: { ref?: React.Ref<HTMLDivElement>; asChild?: boolean } & React.HTMLAttributes<HTMLDivElement>) {
	const Component = asChild ? Slot : "div";

	return (
		<Component
			className={cn(
				"relative rounded-lg bg-card border text-card-foreground shadow-sm",
				className
			)}
			ref={reference}
			{...props}
		/>
	);
}
Card.displayName = "Card";

function CardHeader({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn("flex flex-col gap-1.5 p-6", className)}
			ref={reference}
			{...props}
		/>
	);
}
CardHeader.displayName = "CardHeader";

function CardTitle({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLSpanElement> } & React.HTMLAttributes<HTMLSpanElement>) {
	return (
		<span
			className={cn(
				"text-xl font-semibold leading-none tracking-tight",
				className
			)}
			ref={reference}
			{...props}
		/>
	);
}
CardTitle.displayName = "CardTitle";

function CardDescription({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn("text-sm text-muted-foreground", className)}
			ref={reference}
			{...props}
		/>
	);
}
CardDescription.displayName = "CardDescription";

function CardContent({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement>) {
	return <div className={cn("p-6 pt-0", className)} ref={reference} {...props} />;
}
CardContent.displayName = "CardContent";

function CardFooter({ ref: reference, className, ...props }: { ref?: React.Ref<HTMLDivElement> } & React.HTMLAttributes<HTMLDivElement>) {
	return (
		<div
			className={cn("flex items-center p-6 border-t", className)}
			ref={reference}
			{...props}
		/>
	);
}
CardFooter.displayName = "CardFooter";

export const CardIndicator: React.FC<React.PropsWithChildren<{ className?: string; icon?: React.ReactNode }>> = ({ className, icon, children }) => (
	<div className={twMerge("flex size-fit items-center justify-center gap-2 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 px-4 py-0.5 text-foreground", className)}>
		<Slot className="size-4">{icon}</Slot>
		<span className="text-xs font-medium">
			{children}
		</span>
	</div>
);

export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
